import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["data-base-equalizer"]

import { getRandomId } from '@/utils/BaseHelper';
import {
  onMounted, ref,
} from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseEqualizer',
  props: {
  slug: {
    type: String,
    default: null,
  },
},
  setup(__props, { expose: __expose }) {

const root = ref();
const randomId = getRandomId();



const elementIsVisible = (element: HTMLElement): boolean => !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
const rootIsVisible = (): boolean => elementIsVisible(root.value);
const nodeListChildren = (elements: HTMLElement[], querySelector: string): HTMLElement[] => {
  const children: HTMLElement[] = [];

  elements.forEach((el: HTMLElement) => {
    children.push(...Array.from(el.querySelectorAll(querySelector)) as HTMLElement[]);
  });

  return children;
};
const nodeListSetStyleProperty = (elements: HTMLElement[], property: string, value: string): void => {
  elements.forEach((element) => {
    element.style.setProperty(property, value);
  });
};
const nodeListRemoveStyleProperty = (elements: HTMLElement[], property: string): void => {
  elements.forEach((element) => {
    element.style.removeProperty(property);
  });
};
const nodeListFilterByClass = (elements: HTMLElement[], className: string): HTMLElement[] => [...elements].filter(
  (element) => element.classList.contains(className),
);
const nodeListFilterByMissingClass = (elements: HTMLElement[], className: string): HTMLElement[] => [...elements].filter(
  (element) => !element.classList.contains(className),
);
const getRowElements = (top: number, elements: HTMLElement[]): HTMLElement[] => [...elements].filter((element) => {
  const rect = element.getBoundingClientRect();

  return (rect.top >= top - 16 && rect.top <= top + 16);
});
const equalizeRows = (rowElements: HTMLElement[]): void => {
  const rowElementsEqParts = nodeListChildren(rowElements, '[data-base-equalizer-element-part]');
  const rowElementsEqPartGroups = nodeListChildren(rowElements, '[data-base-equalizer-element-group]');

  if (rowElementsEqParts.length) {
    const eqPartKeys: string[] = [];
    rowElementsEqParts.forEach((el) => {
      const { baseEqualizerElementPart } = el.dataset;

      if (baseEqualizerElementPart && eqPartKeys.indexOf(baseEqualizerElementPart) === -1) {
        eqPartKeys.push(baseEqualizerElementPart);
      }
    });

    const eqPartGroups: string[] = [];
    rowElementsEqPartGroups.forEach((el) => {
      const { baseEqualizerElementGroup } = el.dataset;

      if (baseEqualizerElementGroup && eqPartGroups.indexOf(baseEqualizerElementGroup) === -1) {
        eqPartGroups.push(baseEqualizerElementGroup);
      }
    });

    eqPartKeys.forEach((partKey: string) => {
      let minHeight = 0;
      const eqPartElements = nodeListChildren(rowElements, `[data-base-equalizer-element-part*="${partKey}"]`);

      eqPartElements.forEach((el: HTMLElement) => {
        const elHeight = el.clientHeight;
        if (elHeight > minHeight) {
          minHeight = elHeight;
        }
      });

      nodeListSetStyleProperty(eqPartElements, 'min-height', `${minHeight}px`);
    });

    eqPartGroups.forEach((group) => {
      const groupKeys = group.split(',');
      let minHeight = 0;

      groupKeys.forEach((groupKey: string) => {
        const eqPartGroupElements = nodeListChildren(rowElements, `[data-base-equalizer-element-part*="${groupKey}"]`);

        minHeight += eqPartGroupElements[0].clientHeight;
      });

      const eqPartGroupElements = nodeListChildren(rowElements, `[data-base-equalizer-element-group*="${group}"]`);

      nodeListSetStyleProperty(eqPartGroupElements, 'min-height', `${minHeight}px`);

      let groupMaxHeight = 0;

      eqPartGroupElements.forEach((el) => {
        if (el.clientHeight > groupMaxHeight && el.clientHeight > minHeight) {
          groupMaxHeight = el.clientHeight;
        }
      });

      if (groupMaxHeight > 0) {
        nodeListSetStyleProperty(eqPartGroupElements, 'min-height', `${groupMaxHeight}px`);

        eqPartGroupElements.forEach((element: HTMLElement) => {
          let partsHeight = 0;
          const elEqParts = nodeListChildren([element], '[data-base-equalizer-element-part]')
            .filter((el: HTMLElement) => el.dataset.eqPart && groupKeys.indexOf(el.dataset.eqPart) !== -1);

          const elEqPartsFirst = elEqParts.filter((el: HTMLElement) => el.dataset.eqPart === groupKeys[0]);
          if (elEqParts.length === groupKeys.length) {
            elEqParts.forEach((el: HTMLElement) => {
              partsHeight += el.clientHeight;
            });

            if (partsHeight < groupMaxHeight) {
              elEqPartsFirst.forEach((el: HTMLElement) => {
                element.style.setProperty('min-height', `${el.clientHeight + (groupMaxHeight - partsHeight)}px`);
              });
            }
          }
        });
      }
    });
  } else {
    let minHeight = 0;

    rowElements.forEach((el) => {
      if (el.clientHeight > minHeight) {
        minHeight = el.clientHeight;
      }
    });

    nodeListSetStyleProperty(rowElements, 'min-height', `${minHeight}px`);
  }

  rowElements.forEach((el) => {
    el.classList.add('equalized');
  });
};

const runEqualize = (): void => {
  if (!root.value) return;
  if (!rootIsVisible()) return;

  const eqElements = [...root.value.querySelectorAll('[data-base-equalizer-element]')].filter(
    (element: HTMLElement) => elementIsVisible(element),
  );

  eqElements.forEach((element: HTMLElement) => {
    element.classList.remove('equalized');
  });

  const eqElementsChildren = nodeListChildren(eqElements, '[data-base-equalizer-element-part]');

  if (!eqElementsChildren.length) {
    nodeListRemoveStyleProperty(eqElements as HTMLElement[], 'min-height');
  } else {
    nodeListRemoveStyleProperty(eqElementsChildren, 'min-height');
  }

  while (nodeListFilterByClass(eqElements as HTMLElement[], 'equalized').length < eqElements.length) {
    const firstOfRow = nodeListFilterByMissingClass(eqElements as HTMLElement[], 'equalized')[0];

    const rect = firstOfRow.getBoundingClientRect();
    const { top } = rect;

    const rowElements = getRowElements(top, eqElements as HTMLElement[]);

    equalizeRows(rowElements);
  }
};

const resizeObserver = new ResizeObserver(() => {
  runEqualize();
});

onMounted(() => {
  if (root.value) {
    runEqualize();
    resizeObserver.observe(root.value);
  }
});

__expose({
  runEqualize,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    "data-base-equalizer": _unref(randomId)
  }, [
    _renderSlot(_ctx.$slots, "default")
  ], 8, _hoisted_1))
}
}

})